// ── Adapter Supabase do ISalesRepository ──────────────────────────
// Única camada que conhece a RPC register_card_sale_from_proposal e as
// tabelas cards/pipeline_stages para o reposicionamento do card.

import { getSupabase } from '@infra/supabase/client';
import type { Json } from '@/types/database.types';
import type {
  ISalesRepository,
  RegisterCardSaleParams,
  SaleRpcResult,
  CardPipelineStage,
} from '@modules/sales';

const requireSupabase = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabaseSalesRepository implements ISalesRepository {
  registerCardSaleFromProposal = async (params: RegisterCardSaleParams): Promise<SaleRpcResult> => {
    const supabase = requireSupabase();
    const { data, error } = await supabase.rpc('register_card_sale_from_proposal', {
      p_card_id:                 params.cardId,
      p_faturamento:             params.faturamento,
      p_proposta:                (params.proposta ?? null) as Json,
      p_meeting_type:            params.meetingType || 'perpetuo',
      p_closer_as_sdr:           params.closerAsSdr ? true : undefined,
      p_is_turbo:                params.isTurbo,
      p_data_venda:              params.dataVenda || undefined,
      p_tem_entrada:             params.temEntrada,
      p_valor_entrada:           params.temEntrada ? params.valorEntrada : undefined,
      p_data_pagamento_restante: params.dataPagamentoRestante || undefined,
      p_forma_pagamento:         params.formaPagamento || undefined,
      p_notes:                   params.notes || undefined,
      p_plan_lines:              (params.planLines ?? null) as Json,
    });
    if (error) throw error;
    return (data ?? null) as unknown as SaleRpcResult;
  };

  getCardPipelineStage = async (cardId: string): Promise<CardPipelineStage | null> => {
    const { data } = await requireSupabase()
      .from('cards')
      .select('pipeline_id, stage_id')
      .eq('id', cardId)
      .single();
    return (data ?? null) as CardPipelineStage | null;
  };

  getLastActiveStageId = async (pipelineId: string): Promise<string | null> => {
    const { data } = await requireSupabase()
      .from('pipeline_stages')
      .select('id')
      .eq('pipeline_id', pipelineId)
      .eq('ativo', true)
      .order('order_index', { ascending: false })
      .limit(1);
    return data?.[0]?.id ?? null;
  };

  updateCardStage = async (cardId: string, stageId: string): Promise<void> => {
    await requireSupabase().from('cards').update({ stage_id: stageId }).eq('id', cardId);
  };
}
